import React, { useCallback, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"

import { useAgentsById } from "@/agents/catalog"
import { SORT_DIRECTION } from "@/constants/sort"
import {
  useHumanReviewProjectMutationsTransport,
  useHumanReviewProjectsTransport,
  useHumanReviewProjectWithSimulationIdsFetcherTransport,
  useProjectAssignmentEmailTransport,
} from "@/human-feedback/transport"
import {
  MetricDefinitionsLoadWarning,
  resolveMetricReferenceKeys,
  useMetricRecordsById,
} from "@/metrics/registry"
import { useCreatorResolver } from "@/organization"
import { usePersonasById } from "@/persona"
import {
  useDeleteRunTransport,
  useRunTransportInvalidation,
  useSimulationOutputIdsFetcherTransport,
} from "@/runs"
import { useResourceDrawer } from "@/runs/resource-drawers"
import { useTestSetsById } from "@/test-set/catalog"
import { useOrganization } from "@clerk/nextjs"
import { Button, cn, TABLE_CARD_CLASS, type SortDescriptor } from "@coval/ui"

import { TableDisplayScope } from "@/components/tables/TableDisplayScope"
import { useQueryParams } from "@/hooks/useQueryParams"
import { formatSimulatorType } from "@/utils/formatting/format-simulator-type"

import { useRunsFilter } from "../contexts/RunsFilterContext"
import { useBulkRunDelete } from "../hooks/useBulkRunDelete"
import { useRunsMetricPresenceFilter } from "../hooks/useRunsMetricPresenceFilter"
import { useRunsPageQueries } from "../hooks/useRunsPageQueries"
import { useRunsSessionState } from "../hooks/useRunsSessionState"
import { useRunsTableCellLookups } from "../hooks/useRunsTableCellLookups"
import { useRunsTableColumnControls } from "../hooks/useRunsTableColumnControls"
import { useRunsTableColumns } from "../hooks/useRunsTableColumns"
import { useRunsTableCsvDownload } from "../hooks/useRunsTableCsvDownload"
import { useRunsTableInteractions } from "../hooks/useRunsTableInteractions"
import { useRunsTableNavigation } from "../hooks/useRunsTableNavigation"
import { useRunsTableSelection } from "../hooks/useRunsTableSelection"
import { useRunsViewModeState } from "../hooks/useRunsViewModeState"
import type { RunsSavedViewsTransportState } from "../types/savedViews"
import { RUNS_VIEW_MODE } from "../types/viewMode"
import {
  buildDisplayNameLookup,
  buildRunsKeysetFilters,
  getEffectiveAgentFilters,
  getRowsForQueryState,
  getRunsSortColumn,
  getRunsSortColumnId,
  getRunsSortDirection,
  shouldShowPlaceholderRows,
} from "../utils/runsTableState"
import { getRunsFilterValidationError } from "../utils/transportRunsFilters"
import {
  computeMaxTagCount,
  computeTagsColumnWidth,
} from "../utils/virtualRunsTableLayout"
import { RunsHeaderActions } from "./RunsHeaderActions"
import { RunsSummaryPublisher } from "./RunsSummaryPublisher"
import { buildRunColumns, buildSimulationColumns } from "./RunsTableColumns"
import {
  RunsTableControls,
  RunsTableDataView,
  RunsTableDialogs,
} from "./RunsTablePresentation"

interface RunsTableProps {
  initialCount?: number
  isCountLoading?: boolean
  /** Filter to a specific scheduled run ID */
  scheduledRunId?: string
  /** Replaces the default empty state, for a caller whose rows are not user-launched. */
  emptyState?: React.ReactNode
  /** Hide filter controls, search, view mode toggle - show only the table */
  hideControls?: boolean
  savedViewsTransport?: RunsSavedViewsTransportState
}

export const RunsTable = React.memo<RunsTableProps>(
  ({
    scheduledRunId,
    emptyState,
    hideControls = false,
    savedViewsTransport,
  }: RunsTableProps = {}) => {
    const router = useRouter()
    const queryParams = useQueryParams()
    const { organization } = useOrganization()
    const searchInputRef = useRef<HTMLInputElement>(null)
    const [isOpen, setIsOpen] = useState(false)
    const onOpen = useCallback(() => setIsOpen(true), [])
    const onClose = useCallback(() => setIsOpen(false), [])
    const [isReportLimitOpen, setReportLimitOpen] = useState(false)
    const onReportLimitOpen = useCallback(() => setReportLimitOpen(true), [])
    const onReportLimitClose = useCallback(() => setReportLimitOpen(false), [])
    const {
      data: humanReviewProjects = [],
      isPending: isProjectsLoading,
      isError: isProjectsError,
      refetch: refetchProjects,
    } = useHumanReviewProjectsTransport({
      enabled: !hideControls,
    })
    const fetchHumanReviewProjectWithSimulationIds =
      useHumanReviewProjectWithSimulationIdsFetcherTransport()
    const { updateProject: updateHumanReviewProject } =
      useHumanReviewProjectMutationsTransport()
    const { mutateAsync: sendProjectUpdate } =
      useProjectAssignmentEmailTransport()
    const fetchSimulationOutputIdsForRun =
      useSimulationOutputIdsFetcherTransport()
    const viewModeState = useRunsViewModeState({ hideControls })
    const { viewMode, handleViewModeChange: changeViewMode } = viewModeState
    const {
      searchValue,
      searchResetKey,
      debouncedSearchValue,
      sortDescriptor,
      setSortDescriptor,
      setSearchValue,
      statusFilters,
      templateFilters,
      personaFilters,
      agentFilters,
      metricFilters,
      metricValueFilters,
      tagFilters,
      mutationFilters,
      testSetFilters,
      testCaseFilters,
      metadataFilters,
      scheduledJobFilters,
      dateRange,
      totalActiveFilters,
      hasActiveFilters: hasContextFilters,
      clearAllFilters,
    } = useRunsFilter()
    const filterValidationError = getRunsFilterValidationError({
      metadataFilters,
    })
    const onSearchChange = useCallback(
      (value: string): void => {
        setSearchValue(value)
      },
      [setSearchValue],
    )
    const onClear = useCallback((): void => {
      setSearchValue("")
    }, [setSearchValue])
    const sessionState = useRunsSessionState()

    // Resolves legacy metric-name filters to the IDs used by runs queries.
    const hasOrganizationId = Boolean(organization?.id)
    const {
      metricDefinitions: catalogMetricDefinitions,
      metricIdsFilter,
      metricDisplayNames,
      queriesEnabled: tableQueriesEnabled,
    } = useRunsMetricPresenceFilter(metricFilters, hasOrganizationId)
    const isMetricRecordsLoading = catalogMetricDefinitions.isLoading
    const sortColumn = useMemo(
      () => getRunsSortColumn(sortDescriptor),
      [sortDescriptor],
    )
    const sortDirection = useMemo(
      () => getRunsSortDirection(sortDescriptor),
      [sortDescriptor],
    )
    const selectedAgentId = queryParams.get("agent_id")
    const hasActiveFilters = hasContextFilters || Boolean(selectedAgentId)
    const hasPropertyFilters =
      totalActiveFilters > Number(Boolean(searchValue.trim()))
    // A site-wide agent filter locks the agent selector to that value.
    const effectiveAgentFilters = useMemo(
      () => getEffectiveAgentFilters(selectedAgentId, agentFilters),
      [selectedAgentId, agentFilters],
    )
    const { testSetsById } = useTestSetsById({ enabled: hasOrganizationId })
    const { agentsById } = useAgentsById({ enabled: hasOrganizationId })
    const { personasById } = usePersonasById({ enabled: hasOrganizationId })
    const {
      openAgentDrawer,
      openTestSetDrawer,
      openPersonaDrawer,
      drawerElement,
    } = useResourceDrawer()
    const { resolveCreator } = useCreatorResolver()
    const keysetFilters = useMemo(
      () =>
        buildRunsKeysetFilters({
          dateRange,
          debouncedSearchValue,
          effectiveAgentFilters,
          metricIdsFilter,
          metricValueFilters,
          mutationFilters,
          personaFilters,
          metadataFilters,
          scheduledJobFilters,
          scheduledRunId,
          sortColumn,
          sortDirection,
          statusFilters,
          tagFilters,
          templateFilters,
          testCaseFilters,
          testSetFilters,
        }),
      [
        dateRange,
        debouncedSearchValue,
        effectiveAgentFilters,
        metricIdsFilter,
        metricValueFilters,
        mutationFilters,
        personaFilters,
        metadataFilters,
        scheduledJobFilters,
        scheduledRunId,
        sortColumn,
        sortDirection,
        statusFilters,
        tagFilters,
        templateFilters,
        testCaseFilters,
        testSetFilters,
      ],
    )

    const {
      allRuns,
      allSimulations,
      referenceRows,
      loadedCursors,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
      isLoading,
      isFetching,
      isError,
      refetch,
      filterQueriesEnabled,
      isPlaceholderData,
      tableHasInitiallyLoaded,
    } = useRunsPageQueries({
      viewMode,
      filters: keysetFilters,
      enabled: tableQueriesEnabled,
      includeSummarySimulations: false,
    })
    const metricDefinitions = useMetricRecordsById({
      enabled: hasOrganizationId,
      referenceRows,
    })
    const { metricRecordsById, metricIdAliases } = metricDefinitions
    const isResultsLoading =
      isLoading ||
      (metricFilters.length > 0 &&
        isMetricRecordsLoading &&
        !catalogMetricDefinitions.isError) ||
      (metricDefinitions.isLoading && !metricDefinitions.isError)
    const queryPlaceholderState = useMemo(
      () => ({ isFetching, isPlaceholderData }),
      [isFetching, isPlaceholderData],
    )
    const isShowingPlaceholderRows = shouldShowPlaceholderRows(
      queryPlaceholderState,
    )
    const [tableRuns, tableSimulations] = useMemo(
      () =>
        [
          getRowsForQueryState(allRuns, queryPlaceholderState).map((row) =>
            resolveMetricReferenceKeys(row, metricIdAliases),
          ),
          getRowsForQueryState(allSimulations, queryPlaceholderState).map(
            (row) => resolveMetricReferenceKeys(row, metricIdAliases),
          ),
        ] as const,
      [allRuns, allSimulations, queryPlaceholderState, metricIdAliases],
    )
    const tableHasNextPage = isPlaceholderData
      ? isShowingPlaceholderRows && hasNextPage
      : hasNextPage
    const allRunIds = useMemo(() => tableRuns.map((run) => run.id), [tableRuns])
    const allSimulationOutputIds = useMemo(
      () =>
        tableSimulations.map((simulation) => simulation.simulation_output_id),
      [tableSimulations],
    )
    const { invalidateRunsLists } = useRunTransportInvalidation()
    const deleteRunMutation = useDeleteRunTransport()
    const testSetNames = useMemo(
      () => buildDisplayNameLookup(testSetsById),
      [testSetsById],
    )
    const agentNames = useMemo(
      () => buildDisplayNameLookup(agentsById),
      [agentsById],
    )
    const {
      activeColumns,
      allColumns,
      baseRunColumns,
      baseSimulationColumns: baseSimColumns,
      setVisibleColumns,
      simulationColumns,
      visibleColumns,
    } = useRunsTableColumns({
      viewMode,
      queryContext: keysetFilters,
      runs: tableRuns,
      simulations: tableSimulations,
      metricDisplayNames,
      metricsMetadataReady: !isMetricRecordsLoading,
    })

    const currentItemsLength =
      viewMode === RUNS_VIEW_MODE.RUNS
        ? tableRuns.length
        : tableSimulations.length

    const {
      clearSelectedKeys,
      handleClearSelection,
      handleExitSelectMode,
      isSelectMode,
      runsToDelete,
      selectedCount,
      selectedKeys,
      setRunsToDelete,
      setSelectedKeys,
    } = useRunsTableSelection({
      allItemsCount: currentItemsLength,
    })
    const buildRunsHref = useCallback(
      (path: string) => queryParams.getHref(path, organization?.slug ?? ""),
      [organization?.slug, queryParams],
    )
    const navigateToHref = useCallback(
      (url: string) => {
        void router.push(url)
      },
      [router],
    )

    const {
      handleSimulate,
      handleRowClick,
      handleScrollPositionChange,
      handleSimulationRowClick,
      initialScrollOffset,
      openRun,
      openSimulation,
      resetScrollPosition,
      runHref,
      simulationHref,
    } = useRunsTableNavigation({
      buildHref: buildRunsHref,
      isSelectMode,
      loadedCursors,
      navigate: navigateToHref,
      onRestoreViewMode: changeViewMode,
      sessionState,
      urlViewMode: viewModeState.urlViewMode,
      viewMode,
    })

    const {
      activeRowId,
      handleCompareRuns,
      handlePointerEnter,
      handlePointerLeave,
      handleReportLimitConfirm,
      handleViewModeChange,
      selectedKeySet,
      selectedRunIds,
    } = useRunsTableInteractions({
      allItemIds:
        viewMode === RUNS_VIEW_MODE.RUNS ? allRunIds : allSimulationOutputIds,
      buildRunsHref,
      changeViewMode,
      handleClearSelection,
      handleExitSelectMode,
      hideControls,
      isSelectMode,
      isShowingPlaceholderRows,
      navigateToHref,
      onReportLimitClose,
      onReportLimitOpen,
      openRun,
      openSimulation,
      resetScrollPosition,
      selectedCount,
      selectedKeys,
      tableRuns,
      tableSimulations,
      viewMode,
    })

    const deleteRunCallback = useCallback(
      (runId: string) => deleteRunMutation.mutateAsync({ run_id: runId }),
      [deleteRunMutation],
    )
    const invalidateRunsTablesCallback = useCallback(async () => {
      await invalidateRunsLists()
    }, [invalidateRunsLists])
    const {
      confirmBulkDelete,
      failedRunIds,
      hasPartialSuccess,
      isDeleting: isBulkDeleting,
      resetBulkDeleteState,
    } = useBulkRunDelete({
      clearSelectedKeys,
      closeModal: onClose,
      deleteRun: deleteRunCallback,
      invalidateRunsTables: invalidateRunsTablesCallback,
      runsToDelete,
      setRunsToDelete,
    })

    const handleBulkDelete = useCallback((): void => {
      if (selectedRunIds.length === 0) {
        return
      }

      resetBulkDeleteState()
      setRunsToDelete(selectedRunIds)
      onOpen()
    }, [onOpen, resetBulkDeleteState, selectedRunIds, setRunsToDelete])

    const handleConfirmDelete = useCallback(() => {
      void confirmBulkDelete()
    }, [confirmBulkDelete])

    const handleDeleteModalClose = useCallback(() => {
      const closeDeleteModal = async () => {
        if (hasPartialSuccess) {
          await invalidateRunsTablesCallback()
        }

        resetBulkDeleteState()
        onClose()
      }

      void closeDeleteModal()
    }, [
      hasPartialSuccess,
      invalidateRunsTablesCallback,
      onClose,
      resetBulkDeleteState,
    ])

    const cellLookups = useRunsTableCellLookups({
      agentNames,
      testSetNames,
      metricDisplayNames,
      metricRecordsById,
      personasById,
      getModelType: formatSimulatorType,
      onAgentClick: openAgentDrawer,
      onTestSetClick: openTestSetDrawer,
      onPersonaClick: openPersonaDrawer,
      resolveCreator,
    })

    // The tags column sizes to the widest tag set in the loaded rows.
    const tagsColumnWidth = useMemo(() => {
      const rows =
        viewMode === RUNS_VIEW_MODE.RUNS ? tableRuns : tableSimulations
      return computeTagsColumnWidth(
        computeMaxTagCount(rows.map((row) => row.tags)),
      )
    }, [viewMode, tableRuns, tableSimulations])

    const runColumns = useMemo(
      () =>
        buildRunColumns({
          columns: allColumns,
          tagsColumnWidth,
          lookups: cellLookups,
        }),
      [allColumns, tagsColumnWidth, cellLookups],
    )
    const simColumns = useMemo(
      () =>
        buildSimulationColumns({
          columns: simulationColumns,
          tagsColumnWidth,
          lookups: cellLookups,
        }),
      [simulationColumns, tagsColumnWidth, cellLookups],
    )

    const { downloadCsv: handleDownloadCsv, csvHeaderById } =
      useRunsTableCsvDownload({
        ...cellLookups,
        columns: activeColumns,
        runs: tableRuns,
        simulations: tableSimulations,
        visibleColumns,
        viewMode,
      })

    const {
      columnVisibility,
      handleColumnVisibilityChange,
      runToggleColumns,
      simulationToggleColumns: simToggleColumns,
    } = useRunsTableColumnControls({
      activeColumns,
      baseRunColumns,
      baseSimulationColumns: baseSimColumns,
      runColumns,
      setVisibleColumns,
      simulationColumns: simColumns,
      visibleColumns,
    })
    // Column headers persist the sort descriptor that drives keyset queries.
    const sortDefault = useMemo<SortDescriptor>(
      () => ({
        columnId: getRunsSortColumnId(sortDescriptor),
        direction: sortDirection === SORT_DIRECTION.ASC ? "asc" : "desc",
      }),
      [sortDescriptor, sortDirection],
    )
    const handleSortChange = useCallback(
      (sort: SortDescriptor | null) => {
        setSortDescriptor({
          column: sort?.columnId ?? "created",
          direction: sort?.direction === "asc" ? "ascending" : "descending",
        })
      },
      [setSortDescriptor],
    )
    return (
      <TableDisplayScope
        search={debouncedSearchValue}
        filters={{
          Status: statusFilters,
          Templates: templateFilters,
          Personas: personaFilters,
          Agents: effectiveAgentFilters,
          Metrics: metricFilters,
          "Metric values": metricValueFilters,
          Tags: tagFilters,
          Mutations: mutationFilters,
          "Test sets": testSetFilters,
          "Test cases": testCaseFilters,
          Metadata: metadataFilters,
          "Scheduled runs": scheduledJobFilters,
          Dates: dateRange,
        }}
        className={cn("w-full h-full min-w-0 flex flex-col", TABLE_CARD_CLASS)}
      >
        <MetricDefinitionsLoadWarning
          state={
            catalogMetricDefinitions.isError
              ? catalogMetricDefinitions
              : metricDefinitions
          }
        />
        {!hideControls && (
          <RunsHeaderActions
            onSimulate={handleSimulate}
            scheduledHref={buildRunsHref("/scheduled")}
            templatesHref={buildRunsHref("/templates")}
          />
        )}
        {!hideControls && (
          <RunsSummaryPublisher
            filters={keysetFilters}
            enabled={tableHasInitiallyLoaded && !isFetching && !isError}
            runs={tableRuns}
            simulations={tableSimulations}
            agentNames={agentNames}
            testSetNames={testSetNames}
            personasById={personasById}
            metricDisplayNames={metricDisplayNames}
            metricRecordsById={metricRecordsById}
          />
        )}

        {!hideControls && (
          <RunsTableControls
            clearAllFilters={clearAllFilters}
            clearSelectedKeys={clearSelectedKeys}
            fetchHumanReviewProjectWithSimulationIds={
              fetchHumanReviewProjectWithSimulationIds
            }
            fetchSimulationOutputIdsForRun={fetchSimulationOutputIdsForRun}
            handleBulkDelete={handleBulkDelete}
            handleCompareRuns={handleCompareRuns}
            handleViewModeChange={handleViewModeChange}
            hasActiveFilters={hasPropertyFilters}
            humanReviewProjects={humanReviewProjects}
            isProjectsLoading={isProjectsLoading}
            isProjectsError={isProjectsError}
            onRetryProjects={() => void refetchProjects()}
            isLoading={isResultsLoading}
            isShowingPlaceholderRows={isShowingPlaceholderRows}
            onClear={onClear}
            onSearchChange={onSearchChange}
            savedViewsTransport={savedViewsTransport}
            searchInputRef={searchInputRef}
            searchValue={searchValue}
            searchResetKey={searchResetKey}
            selectedAgentId={selectedAgentId}
            selectedCount={selectedCount}
            selectedKeySet={selectedKeySet}
            sendProjectUpdate={sendProjectUpdate}
            filterQueriesEnabled={filterQueriesEnabled}
            tableRuns={tableRuns}
            tableSimulations={tableSimulations}
            updateHumanReviewProject={updateHumanReviewProject}
            viewMode={viewMode}
          />
        )}

        {filterValidationError ? (
          <div role="alert" className="p-4 text-sm">
            <p>{filterValidationError}</p>
            <Button variant="secondary" onClick={clearAllFilters}>
              Reset filters
            </Button>
          </div>
        ) : (
          <RunsTableDataView
            isError={isError}
            isFetching={isFetching}
            onRetry={() => void refetch()}
            hideControls={hideControls}
            runToggleColumns={runToggleColumns}
            simToggleColumns={simToggleColumns}
            handleColumnVisibilityChange={handleColumnVisibilityChange}
            activeRowId={activeRowId}
            columnVisibility={columnVisibility}
            fetchNextPage={fetchNextPage}
            handleDownloadCsv={handleDownloadCsv}
            handlePointerEnter={handlePointerEnter}
            handlePointerLeave={handlePointerLeave}
            handleRowClick={handleRowClick}
            runHref={runHref}
            simulationHref={simulationHref}
            handleScrollPositionChange={handleScrollPositionChange}
            handleSimulationRowClick={handleSimulationRowClick}
            handleSortChange={handleSortChange}
            csvHeaderById={csvHeaderById}
            hasActiveFilters={hasActiveFilters}
            initialScrollOffset={initialScrollOffset}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isResultsLoading}
            emptyState={emptyState}
            resetLabel={
              hasPropertyFilters || selectedAgentId
                ? "Clear filters"
                : "Clear search"
            }
            onResetFilters={clearAllFilters}
            onSimulate={handleSimulate}
            runColumns={runColumns}
            searchValue={searchValue}
            selectedKeySet={selectedKeySet}
            setSelectedKeys={setSelectedKeys}
            simColumns={simColumns}
            sortDefault={sortDefault}
            tableHasNextPage={tableHasNextPage}
            tableRuns={tableRuns}
            tableSimulations={tableSimulations}
            viewMode={viewMode}
          />
        )}

        <RunsTableDialogs
          failedRunIds={failedRunIds}
          handleConfirmDelete={handleConfirmDelete}
          handleDeleteModalClose={handleDeleteModalClose}
          handleReportLimitConfirm={handleReportLimitConfirm}
          isBulkDeleting={isBulkDeleting}
          isDeleteModalOpen={isOpen}
          isReportLimitOpen={isReportLimitOpen}
          onReportLimitClose={onReportLimitClose}
          reportLimitItemLabel={
            viewMode === RUNS_VIEW_MODE.RUNS ? "runs" : "simulations"
          }
          runsToDeleteCount={runsToDelete.length}
        />
        {drawerElement}
      </TableDisplayScope>
    )
  },
)

RunsTable.displayName = "RunsTable"
